import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Crosshair, RefreshCw, Sparkles, Star, ThumbsDown } from "lucide-react";
import { Api, type SpinHeat, type SpinItem, type DismissalItem } from "../lib/api.js";
import { DismissButton } from "./DismissButton.js";
import { useDismissals } from "../lib/useDismissals.js";

type Mark = "up" | "down" | "hot";

const MARK_META: Record<Mark, { badge: string; title: string }> = {
  hot: { badge: "🎯 closest — want this vibe tonight", title: "Closest to what I want to watch — steers the next spin hard" },
  up: { badge: "⭐ favourite of this six", title: "Favourite of this six — more like it, generally" },
  down: { badge: "👎 least of this six", title: "Least favourite of this six — fewer like it" },
};

/**
 * The magic spinner — a game played six cards at a time.
 *
 * Each six, the viewer marks up to three titles: the FAVOURITE (taste), the
 * LEAST favourite, and the CLOSEST to what they want to watch tonight
 * (current mood). Marks are independent and overlap freely — "I don't love
 * it but it's tonight's vibe" is a legal combination (least + closest on
 * one card). Each mark type is singular per six: placing it on a new card
 * moves it. Clicking a placed mark removes it (the server toggles rows).
 * Then: spin again, and the next six lands closer. The heat chip counts
 * taste-bearing marks so the learning is visible.
 */
export function MagicSpinner({ profileId }: { profileId: string }): JSX.Element | null {
  const [spinning, setSpinning] = useState(false);
  // The three marks of the current six: item id per mark type.
  const [marks, setMarks] = useState<Record<Mark, string | null>>({ up: null, down: null, hot: null });
  const [heatOverride, setHeatOverride] = useState<SpinHeat | null>(null);
  const { dismissedIds, dismiss, dismissing } = useDismissals(profileId);

  const spinQ = useQuery({
    queryKey: ["magic-spin", profileId],
    queryFn: () => Api.spin.get(profileId),
    // Same six on quick home revisits; the spin button is the refresh path.
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });

  const feedback = useMutation({
    mutationFn: (args: { item: SpinItem; verdict: Mark }) =>
      Api.spin.feedback({
        profile_id: profileId,
        item_id: args.item.id,
        title: args.item.title,
        kind: args.item.kind,
        verdict: args.verdict,
      }),
    onSuccess: (d) => setHeatOverride(d.heat),
  });

  const items = (spinQ.data?.items ?? []).filter((item) => !dismissedIds.has(item.id));
  const heat = heatOverride ?? spinQ.data?.heat ?? null;
  const anyMarked = marks.up !== null || marks.down !== null || marks.hot !== null;

  async function respin(): Promise<void> {
    setSpinning(true);
    setMarks({ up: null, down: null, hot: null });
    try {
      await spinQ.refetch();
    } finally {
      // Let the card blur read as a shuffle before the reveal.
      window.setTimeout(() => setSpinning(false), 700);
    }
  }

  /** Place or lift a mark. Singular per six per type; types overlap freely. */
  function toggleMark(item: SpinItem, mark: Mark): void {
    const holder = marks[mark];
    if (holder === item.id) {
      // Lifting the mark off this card.
      setMarks((m) => ({ ...m, [mark]: null }));
      feedback.mutate({ item, verdict: mark });
      return;
    }
    if (holder !== null) {
      // Moving the mark: lift it off its previous card first.
      const prev = items.find((x) => x.id === holder);
      if (prev) feedback.mutate({ item: prev, verdict: mark });
    }
    setMarks((m) => ({ ...m, [mark]: item.id }));
    feedback.mutate({ item, verdict: mark });
  }

  if (spinQ.isLoading) {
    return (
      <section className="mb-10">
        <div className="skeleton h-3 w-40 mb-3" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton aspect-[2/3] rounded-lg" />
          ))}
        </div>
      </section>
    );
  }
  // Keep the spin control reachable when every current pick was dismissed;
  // only hide the section when the server had no candidates at all.
  if (items.length === 0 && (spinQ.data?.items.length ?? 0) === 0) return null;

  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h2 className="section-label inline-flex items-center gap-2">
          <Sparkles size={14} className="text-gold" /> Wallace&rsquo;s magic six
        </h2>
        {heat && (
          <span
            className="text-xs text-parchment/70"
            title={`${heat.count} marks so far — every six sharpens the next`}
          >
            {heat.label === "hot" ? "🔥 hot — tuned to you"
              : heat.label === "warm" ? "♨️ warm"
              : heat.label === "warming" ? "🌡️ warming up"
              : "❄️ cold — mark a few to warm me up"}
          </span>
        )}
        <button
          onClick={() => void respin()}
          disabled={spinning}
          className={`pill-ghost text-xs ml-auto inline-flex items-center gap-1.5 disabled:opacity-50 ${
            anyMarked && !spinning ? "border-gold/70 text-gold animate-pulse" : ""
          }`}
          title={anyMarked ? "Spin a fresh six — your marks are in" : "Spin a fresh six"}
        >
          <RefreshCw size={13} className={spinning ? "animate-spin" : ""} />
          {spinning ? "spinning…" : anyMarked ? "spin with these marks" : "spin again"}
        </button>
      </div>

      <div
        className={`grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 transition-all duration-500 ${
          spinning ? "blur-[3px] saturate-50 scale-[0.99]" : ""
        }`}
      >
        {items.map((item) => (
          <SpinCard
            key={item.id}
            item={item}
            marks={(Object.keys(marks) as Mark[]).filter((m) => marks[m] === item.id)}
            onToggle={toggleMark}
            onDismiss={dismiss}
            dismissing={dismissing}
          />
        ))}
      </div>
      <p className="text-[11px] text-parchment/50 mt-2">
        {items.length === 0
          ? "Current picks dismissed — spin again for a fresh six."
          : "mark a ⭐ favourite, a 👎 least, and a 🎯 closest-to-watch — overlap is fine — then spin."}
      </p>
    </section>
  );
}

function SpinCard({ item, marks, onToggle, onDismiss, dismissing }: {
  item: SpinItem;
  /** Which of the three marks this card currently holds (can be several). */
  marks: Mark[];
  onToggle: (item: SpinItem, mark: Mark) => void;
  onDismiss: (item: DismissalItem) => void;
  dismissing: boolean;
}): JSX.Element {
  // Local series picks land on the series page (episode list); everything
  // else goes straight to the watch page.
  const dest = item.series_category
    ? `/series/${encodeURIComponent(item.series_category)}`
    : `/watch/${encodeURIComponent(item.id)}`;
  const has = (m: Mark): boolean => marks.includes(m);
  return (
    <div
      className={`relative group rounded-lg overflow-hidden border transition-all duration-500 bg-elev ${
        has("hot") ? "border-gold shadow-[0_0_22px_rgba(255,120,40,0.35)]"
          : has("up") ? "border-gold/70"
          : has("down") ? "border-hairline opacity-50 saturate-50"
          : "border-hairline hover:border-gold/50"
      }`}
    >
      <DismissButton itemTitle={item.title} onDismiss={() => onDismiss(item)} disabled={dismissing} />
      <Link to={dest} className="block relative group">
        {item.poster_url ? (
          <img
            src={item.poster_url}
            alt={item.title}
            loading="lazy"
            className="w-full aspect-[2/3] object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-full aspect-[2/3] flex items-center justify-center bg-gradient-to-br from-elev to-ink text-parchment/60 text-3xl font-display">
            {item.title.slice(0, 1)}
          </div>
        )}
        {marks.length > 0 && (
          <div className="absolute top-1.5 left-1.5 flex flex-col gap-1 items-start">
            {marks.map((m) => (
              <span
                key={m}
                className={`text-[10px] font-display tracking-smallcaps px-1.5 py-0.5 rounded ${
                  m === "hot" ? "bg-vermilion text-ivory" : "bg-black/70 text-parchment/90"
                }`}
              >
                {MARK_META[m].badge}
              </span>
            ))}
          </div>
        )}
      </Link>
      <div className="p-2">
        <Link
          to={dest}
          className="block text-xs text-ivory font-display tracking-smallcaps truncate hover:text-gold transition"
          title={item.title}
        >
          {item.title}
        </Link>
        <p className="text-[11px] text-parchment/60 leading-snug line-clamp-2 min-h-[2em] mt-0.5">
          {item.why ?? " "}
        </p>
        <div className="grid grid-cols-3 gap-1 mt-1.5">
          <MarkButton
            active={has("down")}
            onClick={() => onToggle(item, "down")}
            activeClass="bg-vermilion/25 border-vermilionHi text-ivory"
            hoverClass="hover:text-vermilionHi hover:border-vermilion/60"
            title={MARK_META.down.title}
          >
            <ThumbsDown size={12} />
          </MarkButton>
          <MarkButton
            active={has("up")}
            onClick={() => onToggle(item, "up")}
            activeClass="bg-gold/25 border-goldHi text-ivory"
            hoverClass="hover:text-gold hover:border-gold/60"
            title={MARK_META.up.title}
          >
            <Star size={12} />
          </MarkButton>
          <MarkButton
            active={has("hot")}
            onClick={() => onToggle(item, "hot")}
            activeClass="bg-vermilion/40 border-goldHi text-ivory"
            hoverClass="hover:text-gold hover:border-gold/70"
            title={MARK_META.hot.title}
          >
            <Crosshair size={12} />
          </MarkButton>
        </div>
      </div>
    </div>
  );
}

function MarkButton({ children, active, onClick, activeClass, hoverClass, title }: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
  activeClass: string;
  hoverClass: string;
  title: string;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`inline-flex justify-center items-center rounded px-1 py-1 text-[11px] border transition ${
        active ? activeClass : `border-hairline text-parchment ${hoverClass}`
      }`}
    >
      {children}
    </button>
  );
}
